// Collaborations: the negotiation, the conversation and the deliverable loop.
//
// Follows the house convention (inline gql + hand-written interface + thin
// wrapper hook) rather than the codegen pipeline, which emits no hooks and has
// zero importers. useMarketplaceCampaigns.ts is the model.

import { gql, useQuery, useMutation } from "@apollo/client";
import type { SocialPlatform } from "./useMarketplace";
import type { DeliverableFormat, DeliverableSpec } from "./useMarketplaceCampaigns";

export type CollaborationStatus =
  | "NEGOTIATING"
  | "AGREED"
  | "FUNDED"
  | "SHIPPED"
  | "IN_PROGRESS"
  | "SUBMITTED"
  | "REVISION_REQUESTED"
  | "APPROVED"
  | "PUBLISHED"
  | "RELEASED"
  | "DISPUTED"
  | "CANCELLED";

export type OfferParty = "BRAND" | "CREATOR";
export type RateOfferStatus = "PENDING" | "ACCEPTED" | "REJECTED" | "COUNTERED" | "EXPIRED";

/** Whose move it is. Derived server-side on every read, never stored. */
export type TurnOwner = "BRAND" | "CREATOR" | "NOBODY";

export type DeliverableStatus =
  | "PENDING"
  | "SUBMITTED"
  | "REVISION_REQUESTED"
  | "APPROVED"
  | "PUBLISHED"
  | "VERIFIED";

export type ReviewDecision = "APPROVE" | "REQUEST_CHANGES";

export type ApplicationStatus =
  | "PENDING"
  | "SHORTLISTED"
  | "ACCEPTED"
  | "REJECTED"
  | "WITHDRAWN";

export interface RateOffer {
  id: string;
  offeredBy: OfferParty;
  amount: number;
  currency: string;
  message: string | null;
  status: RateOfferStatus;
  aiSuggested: boolean;
  aiRationale: string | null;
  /** Points at the offer this one counters — the chain, not a mutable column. */
  parentOfferId: string | null;
  createdAt: string;
}

export interface CollaborationMessage {
  id: string;
  content: string;
  attachments: string[];
  readAt: string | null;
  senderId: string;
  senderName: string | null;
  createdAt: string;
}

export interface Deliverable {
  id: string;
  platform: SocialPlatform;
  format: DeliverableFormat;
  status: DeliverableStatus;
  quantity: number;
  dueDate: string | null;
  draftAssetUrls: string[];
  publishedUrl: string | null;
  publishedAt: string | null;
  proofScreenshotUrl: string | null;
  reviewNote: string | null;
  revisionCount: number;
  /** False once the campaign's revision allowance is spent. */
  canRequestChanges: boolean;
  metrics: Record<string, number> | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Commission is charged on top of the agreed rate, so `creatorPayout` equals
 * the negotiated number and `brandTotal` is what the brand actually pays.
 */
export interface CollaborationMoney {
  agreedRate: number | null;
  currency: string;
  commissionPct: number;
  commissionAmount: number | null;
  creatorPayout: number | null;
  brandTotal: number | null;
}

export interface CollaborationParticipant {
  id: string;
  name: string;
  handle: string | null;
  logoUrl: string | null;
}

export interface Collaboration {
  id: string;
  status: CollaborationStatus;
  money: CollaborationMoney;
  trackingCode: string;
  dueDate: string | null;
  campaign: { id: string; title: string; maxRevisions: number } | null;
  brand: CollaborationParticipant;
  creator: CollaborationParticipant;
  /** Which side the viewer is on. Gates every action the UI offers. */
  viewerParty: OfferParty;
  turnOwner: TurnOwner;
  offers: RateOffer[];
  pendingOffer: RateOffer | null;
  messages: CollaborationMessage[];
  unreadCount: number;
  deliverables: Deliverable[];
  createdAt: string;
  updatedAt: string;
}

export interface CampaignApplication {
  id: string;
  status: ApplicationStatus;
  pitch: string | null;
  proposedRate: number | null;
  proposedCurrency: string | null;
  createdAt: string;
  creatorProfile: {
    id: string;
    handle: string;
    displayName: string;
    country: string | null;
    avatarUrl: string | null;
    minRate: number | null;
    baseCurrency: string | null;
  } | null;
}

const DELIVERABLE_FIELDS = gql`
  fragment DeliverableFields on Deliverable {
    id
    platform
    format
    status
    quantity
    dueDate
    draftAssetUrls
    publishedUrl
    publishedAt
    proofScreenshotUrl
    reviewNote
    revisionCount
    canRequestChanges
    metrics
    createdAt
    updatedAt
  }
`;

/**
 * The inbox row. Deliberately without `messages` and `offers`: the inbox shows
 * twenty rows, and pulling every message of every thread to render a summary is
 * how a list screen becomes slow.
 */
const COLLABORATION_SUMMARY = gql`
  fragment CollaborationSummary on Collaboration {
    id
    status
    trackingCode
    dueDate
    viewerParty
    turnOwner
    unreadCount
    money {
      agreedRate
      currency
      commissionPct
      commissionAmount
      creatorPayout
      brandTotal
    }
    campaign {
      id
      title
      maxRevisions
    }
    brand {
      id
      name
      handle
      logoUrl
    }
    creator {
      id
      name
      handle
      logoUrl
    }
    pendingOffer {
      id
      offeredBy
      amount
      currency
      status
      createdAt
    }
    createdAt
    updatedAt
  }
`;

const COLLABORATION_FULL = gql`
  ${COLLABORATION_SUMMARY}
  ${DELIVERABLE_FIELDS}
  fragment CollaborationFull on Collaboration {
    ...CollaborationSummary
    offers {
      id
      offeredBy
      amount
      currency
      message
      status
      aiSuggested
      aiRationale
      parentOfferId
      createdAt
    }
    messages {
      id
      content
      attachments
      readAt
      senderId
      senderName
      createdAt
    }
    deliverables {
      ...DeliverableFields
    }
  }
`;

const GET_MY_COLLABORATIONS = gql`
  ${COLLABORATION_SUMMARY}
  query GetMyCollaborations($filter: CollaborationFilterInput) {
    myCollaborations(filter: $filter) {
      ...CollaborationSummary
    }
  }
`;

const GET_COLLABORATION = gql`
  ${COLLABORATION_FULL}
  query GetCollaboration($id: ID!) {
    collaboration(id: $id) {
      ...CollaborationFull
    }
  }
`;

const MAKE_RATE_OFFER = gql`
  mutation MakeRateOffer($input: MakeRateOfferInput!) {
    makeRateOffer(input: $input) {
      id
      amount
      status
    }
  }
`;

const ACCEPT_RATE_OFFER = gql`
  ${COLLABORATION_FULL}
  mutation AcceptRateOffer($offerId: ID!) {
    acceptRateOffer(offerId: $offerId) {
      ...CollaborationFull
    }
  }
`;

const REJECT_RATE_OFFER = gql`
  mutation RejectRateOffer($offerId: ID!) {
    rejectRateOffer(offerId: $offerId) {
      id
      status
    }
  }
`;

const CANCEL_COLLABORATION = gql`
  mutation CancelCollaboration($id: ID!) {
    cancelCollaboration(id: $id) {
      id
      status
    }
  }
`;

const SEND_MESSAGE = gql`
  mutation SendCollaborationMessage($collaborationId: ID!, $content: String!) {
    sendCollaborationMessage(collaborationId: $collaborationId, content: $content) {
      id
      content
      senderId
      senderName
      createdAt
    }
  }
`;

const MARK_READ = gql`
  mutation MarkCollaborationRead($collaborationId: ID!) {
    markCollaborationRead(collaborationId: $collaborationId)
  }
`;

const TRANSLATE_MESSAGE = gql`
  mutation TranslateCollaborationMessage($messageId: ID!, $targetLanguage: String!) {
    translateCollaborationMessage(messageId: $messageId, targetLanguage: $targetLanguage)
  }
`;

const SUBMIT_DRAFT = gql`
  ${DELIVERABLE_FIELDS}
  mutation SubmitDeliverableDraft($id: ID!, $assetUrls: [String!]!) {
    submitDeliverableDraft(id: $id, assetUrls: $assetUrls) {
      ...DeliverableFields
    }
  }
`;

const REVIEW_DELIVERABLE = gql`
  ${DELIVERABLE_FIELDS}
  mutation ReviewDeliverable($id: ID!, $decision: ReviewDecision!, $note: String) {
    reviewDeliverable(id: $id, decision: $decision, note: $note) {
      ...DeliverableFields
    }
  }
`;

const MARK_PUBLISHED = gql`
  ${DELIVERABLE_FIELDS}
  mutation MarkDeliverablePublished(
    $id: ID!
    $publishedUrl: String!
    $proofScreenshotUrl: String
  ) {
    markDeliverablePublished(
      id: $id
      publishedUrl: $publishedUrl
      proofScreenshotUrl: $proofScreenshotUrl
    ) {
      ...DeliverableFields
    }
  }
`;

const VERIFY_DELIVERABLE = gql`
  ${DELIVERABLE_FIELDS}
  mutation VerifyDeliverable($id: ID!) {
    verifyDeliverable(id: $id) {
      ...DeliverableFields
    }
  }
`;

export interface CollaborationFilter {
  status?: CollaborationStatus[];
  campaignId?: string | null;
  awaitingMe?: boolean;
}

export function useMyCollaborations(filter: CollaborationFilter = {}) {
  const { data, loading, error, refetch } = useQuery<{ myCollaborations: Collaboration[] }>(
    GET_MY_COLLABORATIONS,
    { variables: { filter }, fetchPolicy: "cache-and-network" },
  );

  return { collaborations: data?.myCollaborations ?? [], loading, error, refetch };
}

export function useCollaboration(id: string | undefined) {
  const { data, loading, error, refetch } = useQuery<{ collaboration: Collaboration | null }>(
    GET_COLLABORATION,
    { variables: { id }, skip: !id, fetchPolicy: "cache-and-network" },
  );

  return { collaboration: data?.collaboration ?? null, loading, error, refetch };
}

/**
 * Every write on a collaboration.
 *
 * Refetched by operation name so both the open room and the inbox behind it
 * pick the change up — the turn moves on almost every one of these, and an
 * inbox still claiming it is your move is worse than no turn column at all.
 */
export function useCollaborationMutations() {
  const refetch = { refetchQueries: ["GetCollaboration", "GetMyCollaborations"] };

  const [offer, { loading: offering }] = useMutation(MAKE_RATE_OFFER, refetch);
  const [accept, { loading: accepting }] = useMutation(ACCEPT_RATE_OFFER, refetch);
  const [reject, { loading: rejecting }] = useMutation(REJECT_RATE_OFFER, refetch);
  const [cancel, { loading: cancelling }] = useMutation(CANCEL_COLLABORATION, refetch);
  const [send, { loading: sending }] = useMutation(SEND_MESSAGE, refetch);
  const [markRead] = useMutation(MARK_READ);
  const [translate, { loading: translating }] = useMutation(TRANSLATE_MESSAGE);
  const [submit, { loading: submitting }] = useMutation(SUBMIT_DRAFT, refetch);
  const [review, { loading: reviewing }] = useMutation(REVIEW_DELIVERABLE, refetch);
  const [publish, { loading: publishing }] = useMutation(MARK_PUBLISHED, refetch);
  const [verify, { loading: verifying }] = useMutation(VERIFY_DELIVERABLE, refetch);

  return {
    makeOffer: async (collaborationId: string, amount: number, message?: string) => {
      const { data } = await offer({ variables: { input: { collaborationId, amount, message } } });
      return data?.makeRateOffer as RateOffer;
    },
    acceptOffer: async (offerId: string) => {
      const { data } = await accept({ variables: { offerId } });
      return data?.acceptRateOffer as Collaboration;
    },
    rejectOffer: async (offerId: string) => {
      const { data } = await reject({ variables: { offerId } });
      return data?.rejectRateOffer as RateOffer;
    },
    cancelCollaboration: async (id: string) => {
      const { data } = await cancel({ variables: { id } });
      return data?.cancelCollaboration as Collaboration;
    },
    sendMessage: async (collaborationId: string, content: string) => {
      const { data } = await send({ variables: { collaborationId, content } });
      return data?.sendCollaborationMessage as CollaborationMessage;
    },
    // Fire-and-forget: the badge clearing is not worth blocking a render on,
    // and a failure just means it clears on the next visit.
    markRead: (collaborationId: string) => {
      void markRead({ variables: { collaborationId } }).catch(() => undefined);
    },
    translateMessage: async (messageId: string, targetLanguage: string) => {
      const { data } = await translate({ variables: { messageId, targetLanguage } });
      return data?.translateCollaborationMessage as string;
    },
    submitDraft: async (id: string, assetUrls: string[]) => {
      const { data } = await submit({ variables: { id, assetUrls } });
      return data?.submitDeliverableDraft as Deliverable;
    },
    reviewDeliverable: async (id: string, decision: ReviewDecision, note?: string) => {
      const { data } = await review({ variables: { id, decision, note } });
      return data?.reviewDeliverable as Deliverable;
    },
    markPublished: async (id: string, publishedUrl: string, proofScreenshotUrl?: string) => {
      const { data } = await publish({ variables: { id, publishedUrl, proofScreenshotUrl } });
      return data?.markDeliverablePublished as Deliverable;
    },
    verifyDeliverable: async (id: string) => {
      const { data } = await verify({ variables: { id } });
      return data?.verifyDeliverable as Deliverable;
    },
    busy:
      offering ||
      accepting ||
      rejecting ||
      cancelling ||
      sending ||
      translating ||
      submitting ||
      reviewing ||
      publishing ||
      verifying,
  };
}

// ─── Applications and invitations ───────────────────────────────────────────

const APPLICATION_FIELDS = gql`
  fragment ApplicationFields on CampaignApplication {
    id
    status
    pitch
    proposedRate
    proposedCurrency
    createdAt
    creatorProfile {
      id
      handle
      displayName
      country
      avatarUrl
      minRate
      baseCurrency
    }
  }
`;

const GET_CAMPAIGN_APPLICATIONS = gql`
  ${APPLICATION_FIELDS}
  query GetCampaignApplications($campaignId: ID!) {
    campaignApplications(campaignId: $campaignId) {
      ...ApplicationFields
    }
  }
`;

const GET_MY_APPLICATIONS = gql`
  ${APPLICATION_FIELDS}
  query GetMyApplications {
    myApplications {
      ...ApplicationFields
      campaign {
        id
        title
        status
      }
    }
  }
`;

const APPLY_TO_CAMPAIGN = gql`
  ${APPLICATION_FIELDS}
  mutation ApplyToCampaign($input: ApplyToCampaignInput!) {
    applyToCampaign(input: $input) {
      ...ApplicationFields
    }
  }
`;

const WITHDRAW_APPLICATION = gql`
  mutation WithdrawApplication($campaignId: ID!) {
    withdrawApplication(campaignId: $campaignId)
  }
`;

const SHORTLIST_APPLICATION = gql`
  ${APPLICATION_FIELDS}
  mutation ShortlistApplication($applicationId: ID!) {
    shortlistApplication(applicationId: $applicationId) {
      ...ApplicationFields
    }
  }
`;

const REJECT_APPLICATION = gql`
  ${APPLICATION_FIELDS}
  mutation RejectApplication($applicationId: ID!) {
    rejectApplication(applicationId: $applicationId) {
      ...ApplicationFields
    }
  }
`;

const START_COLLABORATION = gql`
  mutation StartCollaboration(
    $campaignId: ID!
    $creatorProfileId: ID!
    $openingRate: Float
    $message: String
  ) {
    startCollaboration(
      campaignId: $campaignId
      creatorProfileId: $creatorProfileId
      openingRate: $openingRate
      message: $message
    ) {
      id
      status
    }
  }
`;

const INVITE_CREATOR_TO_CAMPAIGN = gql`
  mutation InviteCreatorToCampaign($campaignId: ID!, $creatorProfileId: ID!, $message: String) {
    inviteCreatorToCampaign(
      campaignId: $campaignId
      creatorProfileId: $creatorProfileId
      message: $message
    ) {
      id
      status
    }
  }
`;

export interface CampaignInvitation {
  id: string;
  status: "SENT" | "VIEWED" | "ACCEPTED" | "DECLINED" | "EXPIRED";
}

/**
 * An invitation as the creator sees it, with the campaign it is for.
 *
 * `message` is the brand's own words about why they picked this creator. It is
 * the single most useful thing on the opportunities list and the only honest
 * answer to "why me" — there is no match score behind any of this.
 */
export interface MyInvitation extends CampaignInvitation {
  message: string | null;
  expiresAt: string | null;
  createdAt: string;
  campaign: {
    id: string;
    title: string;
    status: string;
    objective: string;
    compensationType: string;
    currency: string;
    budgetTotal: number | null;
    rateMin: number | null;
    rateMax: number | null;
    targetNiches: string[];
    deliverablesSpec: DeliverableSpec[] | null;
    brandProfile: { id: string; companyName: string; logoUrl: string | null } | null;
  } | null;
}

const GET_MY_INVITATIONS = gql`
  query GetMyInvitations {
    myInvitations {
      id
      status
      message
      expiresAt
      createdAt
      campaign {
        id
        title
        status
        objective
        compensationType
        currency
        budgetTotal
        rateMin
        rateMax
        targetNiches
        deliverablesSpec
        brandProfile {
          id
          companyName
          logoUrl
        }
      }
    }
  }
`;

const ACCEPT_INVITATION = gql`
  mutation AcceptInvitation($campaignId: ID!, $proposedRate: Float) {
    acceptInvitation(campaignId: $campaignId, proposedRate: $proposedRate) {
      id
      status
    }
  }
`;

/**
 * The creator's open invitations.
 *
 * `myInvitations` existed on the server with no client reading it, so a brand
 * could invite someone who would never find out. This is that screen's query.
 */
export function useMyInvitations(enabled = true) {
  const { data, loading, error, refetch } = useQuery<{ myInvitations: MyInvitation[] }>(
    GET_MY_INVITATIONS,
    { skip: !enabled, fetchPolicy: "cache-and-network" },
  );

  return { invitations: data?.myInvitations ?? [], loading, error, refetch };
}

/**
 * Accept an invitation, naming a rate.
 *
 * The rate is optional because the creator may want to open the conversation
 * without one; the collaboration lands in NEGOTIATING either way, and the offer
 * chain is where the number actually gets settled.
 */
export function useAcceptInvitation() {
  const [accept, { loading }] = useMutation(ACCEPT_INVITATION, {
    refetchQueries: ["GetMyInvitations", "GetMyCollaborations"],
  });

  return {
    acceptInvitation: async (campaignId: string, proposedRate?: number) => {
      const { data } = await accept({ variables: { campaignId, proposedRate } });
      return data?.acceptInvitation as { id: string; status: CollaborationStatus };
    },
    busy: loading,
  };
}

/**
 * Invite a creator to one of the brand's campaigns.
 *
 * Separate from `useApplicationMutations` because it fires from the public
 * creator profile, which has no application state to refetch — the only thing
 * that changes for the caller is the creator's own invitation list.
 *
 * No rate argument: an invitation carries a message, and the creator names the
 * figure when they accept.
 */
export function useInviteCreator() {
  const [invite, { loading }] = useMutation(INVITE_CREATOR_TO_CAMPAIGN, {
    refetchQueries: ["GetMyCollaborations"],
  });

  return {
    inviteCreator: async (campaignId: string, creatorProfileId: string, message?: string) => {
      const { data } = await invite({ variables: { campaignId, creatorProfileId, message } });
      return data?.inviteCreatorToCampaign as CampaignInvitation;
    },
    busy: loading,
  };
}

export function useCampaignApplications(campaignId: string | undefined, enabled = true) {
  const { data, loading, error, refetch } = useQuery<{
    campaignApplications: CampaignApplication[];
  }>(GET_CAMPAIGN_APPLICATIONS, {
    variables: { campaignId },
    skip: !campaignId || !enabled,
    fetchPolicy: "cache-and-network",
  });

  return { applications: data?.campaignApplications ?? [], loading, error, refetch };
}

export interface MyApplication extends CampaignApplication {
  campaign: { id: string; title: string; status: string } | null;
}

export function useMyApplications(enabled = true) {
  const { data, loading, error, refetch } = useQuery<{ myApplications: MyApplication[] }>(
    GET_MY_APPLICATIONS,
    { skip: !enabled, fetchPolicy: "cache-and-network" },
  );

  return { applications: data?.myApplications ?? [], loading, error, refetch };
}

export function useApplicationMutations() {
  const refetch = {
    refetchQueries: ["GetCampaignApplications", "GetMyApplications", "GetMyCollaborations"],
  };

  const [apply, { loading: applying }] = useMutation(APPLY_TO_CAMPAIGN, refetch);
  const [withdraw, { loading: withdrawing }] = useMutation(WITHDRAW_APPLICATION, refetch);
  const [shortlist, { loading: shortlisting }] = useMutation(SHORTLIST_APPLICATION, refetch);
  const [reject, { loading: rejecting }] = useMutation(REJECT_APPLICATION, refetch);
  const [start, { loading: starting }] = useMutation(START_COLLABORATION, refetch);

  return {
    applyToCampaign: async (campaignId: string, pitch?: string, proposedRate?: number | null) => {
      const { data } = await apply({ variables: { input: { campaignId, pitch, proposedRate } } });
      return data?.applyToCampaign as CampaignApplication;
    },
    withdrawApplication: async (campaignId: string) => {
      const { data } = await withdraw({ variables: { campaignId } });
      return Boolean(data?.withdrawApplication);
    },
    shortlistApplication: async (applicationId: string) => {
      const { data } = await shortlist({ variables: { applicationId } });
      return data?.shortlistApplication as CampaignApplication;
    },
    rejectApplication: async (applicationId: string) => {
      const { data } = await reject({ variables: { applicationId } });
      return data?.rejectApplication as CampaignApplication;
    },
    startCollaboration: async (
      campaignId: string,
      creatorProfileId: string,
      openingRate?: number | null,
      message?: string,
    ) => {
      const { data } = await start({
        variables: { campaignId, creatorProfileId, openingRate, message },
      });
      return data?.startCollaboration as { id: string; status: CollaborationStatus };
    },
    busy: applying || withdrawing || shortlisting || rejecting || starting,
  };
}
